import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';
import { CiSearch } from "react-icons/ci";
import { FiFilter, FiX, FiRefreshCw, FiLayers, FiList, FiGrid, FiClock, FiUser, FiBriefcase, FiCalendar, FiFileText, FiHash, FiDownload } from "react-icons/fi";
import TopNavBar from './TopNavBar';
import MainLayout from './MainLayout';
import '../styles/projectmaster.css';

/* ─── Timesheet View Modal ─── */
function TimesheetViewModal({ ts, onClose }) {
  if (!ts) return null;

  const formatTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d)) return 'N/A';
    const local = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    const timeStr = d.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true
    }).toUpperCase();
    return `${day}-${month}-${year}, ${timeStr}`;
  };

  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d)) return 'N/A';
    const local = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').trim();

  const InfoRow = ({ icon: Icon, label, value, accent }) => (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '12px',
      padding: '12px 0', borderBottom: '1px solid #f1f5f9'
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '8px',
        background: accent ? `${accent}15` : '#f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <Icon size={15} color={accent || '#64748b'} />
      </div>
      <div>
        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: '13.5px', color: '#1e293b', fontWeight: 600 }}>{value || '—'}</div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 9999, padding: 16
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.2)', overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
              Timesheet Detail
            </div>
            <div style={{ color: '#fff', fontSize: '17px', fontWeight: 700 }}>
              {ts.employee_full_name || ts.employeename}
              {ts.employee_id && (
                <span style={{ fontSize: '12px', fontWeight: 500, marginLeft: 8, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '99px' }}>
                  {ts.employee_id}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Hours Badge */}
        <div style={{
          background: '#f8fafc', padding: '14px 24px',
          display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{
            background: ts.hours ? '#ecfdf5' : '#fafafa',
            border: `1px solid ${ts.hours ? '#6ee7b7' : '#e2e8f0'}`,
            borderRadius: '10px', padding: '8px 18px',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <FiClock size={16} color={ts.hours ? '#10b981' : '#94a3b8'} />
            <span style={{ fontWeight: 700, fontSize: '20px', color: ts.hours ? '#059669' : '#94a3b8' }}>
              {ts.hours ? Number(ts.hours).toFixed(2) : '0.00'}
            </span>
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>hours</span>
          </div>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            <span style={{ fontWeight: 600, color: '#334155' }}>{formatDateOnly(ts.starttime)}</span>
            {ts.starttime && ts.endtime && (
              <div style={{ fontSize: '12px', marginTop: 2 }}>
                {new Date(ts.starttime).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}
                {' → '}
                {new Date(ts.endtime).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div style={{ padding: '4px 24px 0 24px' }}>
          <InfoRow icon={FiBriefcase} label="Department" value={ts.department_name} accent="#d97706" />
          <InfoRow icon={FiUser} label="Designation" value={ts.designation} accent="#7c3aed" />
          <InfoRow icon={FiFileText} label="Project" value={ts.project_name || ts.project} accent="#2563eb" />
          <InfoRow icon={FiCalendar} label="Submitted" value={formatTime(ts.starttime)} accent="#db2777" />
        </div>

        {/* Description */}
        {ts.comments && (
          <div style={{ margin: '12px 24px 20px 24px', background: '#f8fafc', borderRadius: '10px', padding: '14px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: 8 }}>Work Description</div>
            <div style={{ fontSize: '13.5px', color: '#334155', lineHeight: 1.6 }}>
              {stripHtml(ts.comments)}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '12px 24px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px',
              padding: '10px 24px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AllTimesheets() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'kanban'
  const [selectedTs, setSelectedTs] = useState(null); // selected timesheet for view modal

  // Basic search
  const [searchQuery, setSearchQuery] = useState('');

  // Advanced Filter state
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    department: '',
    designation: '',
    project: '',
    employee: '',
    dateFrom: '',
    dateTo: ''
  });

  // Group By State
  const [groupBy, setGroupBy] = useState('project'); // project | department | designation | employee
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const groupByRef = useRef(null);

  const stripHtmlRaw = (html) => (html || '').replace(/<[^>]*>/g, '').trim();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const exportTimesheetsCSV = () => {
    if (filteredData.length === 0) return alert("No timesheets to export.");
    const csvData = filteredData.map(ts => ({
      "Employee": ts.employee_full_name || ts.employeename || "",
      "Employee ID": ts.employee_id || "",
      "Department": ts.department_name || "",
      "Designation": ts.designation || "",
      "Project": ts.project_name || ts.project || "",
      "Date": formatDisplayDate(ts.starttime),
      "Start Time": formatTime(ts.starttime).split(',')[1] || "",
      "End Time": formatTime(ts.endtime).split(',')[1] || "",
      "Hours": ts.hours ? Number(ts.hours).toFixed(2) : "0.00",
      "Description": stripHtmlRaw(ts.comments)
    }));

    const headers = Object.keys(csvData[0]);
    const titleRow = `"Timesheets Report"${','.repeat(headers.length - 1)}`;
    const csvRows = [titleRow, headers.join(',')];
    for (const row of csvData) {
      csvRows.push(headers.map(h => `"${(row[h] || "").toString().replace(/"/g, '""')}"`).join(','));
    }

    const blob = new Blob([csvRows.join('\\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Timesheets_Export_${formatDate(new Date())}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchAllTimesheets();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (groupByRef.current && !groupByRef.current.contains(e.target)) setShowGroupByDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchAllTimesheets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/timesheet/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimesheets(response.data.data || []);
    } catch (error) {
      console.error('Failed to load all timesheets', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d)) return 'N/A';
    const local = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    const timeStr = d.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true
    }).toUpperCase();
    return `${day}-${month}-${year}, ${timeStr}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d)) return 'N/A';
    const local = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toISOString().split('T')[0];
  };

  // Unique options for filters
  const departments = [...new Set(timesheets.map(t => t.department_name).filter(Boolean))];
  const designations = [...new Set(timesheets.map(t => t.designation).filter(Boolean))];
  const projects = [...new Set(timesheets.map(t => t.project_name || t.project).filter(Boolean))];
  const employees = [...new Set(timesheets.map(t => t.employee_full_name || t.employeename).filter(Boolean))].map(name => {
    const ts = timesheets.find(t => (t.employee_full_name || t.employeename) === name);
    return { name, id: ts?.employee_id || 'N/A', dept: ts?.department_name || '—', desig: ts?.designation || '—' };
  });

  const getQuickDate = (type) => {
    const today = new Date();
    if (type === 'today') return formatDate(today);
    if (type === 'yesterday') {
      const y = new Date(today); y.setDate(today.getDate() - 1);
      return formatDate(y);
    }
    return '';
  };

  const applyQuickDate = (type) => {
    if (type === 'today') {
      const d = getQuickDate('today');
      setAdvancedFilters(f => ({ ...f, dateFrom: d, dateTo: d }));
    } else if (type === 'yesterday') {
      const d = getQuickDate('yesterday');
      setAdvancedFilters(f => ({ ...f, dateFrom: d, dateTo: d }));
    } else if (type === 'this_week') {
      const d = new Date();
      const first = d.getDate() - d.getDay();
      const firstDay = new Date(d.setDate(first));
      const lastDay = new Date(d.setDate(first + 6));
      setAdvancedFilters(f => ({ ...f, dateFrom: formatDate(firstDay), dateTo: formatDate(lastDay) }));
    } else if (type === 'this_month') {
      const d = new Date();
      const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      setAdvancedFilters(f => ({ ...f, dateFrom: formatDate(firstDay), dateTo: formatDate(lastDay) }));
    }
  };

  const handleResetFilters = () => {
    setAdvancedFilters({ department: '', designation: '', project: '', employee: '', dateFrom: '', dateTo: '' });
  };

  // Filter Data
  const filteredData = useMemo(() => {
    return timesheets.filter(ts => {
      const empName = ts.employee_full_name || ts.employeename || "";
      const projName = ts.project_name || ts.project || "";

      const matchSearch = searchQuery === '' || empName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchDept = advancedFilters.department === '' || ts.department_name === advancedFilters.department;
      const matchDesig = advancedFilters.designation === '' || ts.designation === advancedFilters.designation;
      const matchProject = advancedFilters.project === '' || projName === advancedFilters.project;
      const matchEmployee = advancedFilters.employee === '' || empName === advancedFilters.employee;

      let matchDate = true;
      const tsDate = ts.starttime ? formatDate(ts.starttime) : "";
      if (advancedFilters.dateFrom && tsDate < advancedFilters.dateFrom) matchDate = false;
      if (advancedFilters.dateTo && tsDate > advancedFilters.dateTo) matchDate = false;

      return matchSearch && matchDept && matchDesig && matchProject && matchEmployee && matchDate;
    });
  }, [timesheets, searchQuery, advancedFilters]);

  // Dynamic Grouping
  const groupedData = useMemo(() => {
    const map = {};
    const colors = ["#6366f1", "#0f766e", "#db2777", "#d97706", "#2563eb", "#65a30d", "#9333ea"];
    const bgs = ["#eef2ff", "#ccfbf1", "#fce7f3", "#fef3c7", "#dbeafe", "#ecfccb", "#f3e8ff"];

    filteredData.forEach(ts => {
      let key = groupBy === "none" ? "All Timesheets" :
        groupBy === "project" ? (ts.project_name || ts.project || "Unassigned") :
          groupBy === "department" ? (ts.department_name || "Unassigned") :
            groupBy === "designation" ? (ts.designation || "Unassigned") :
              groupBy === "employee" ? (ts.employee_full_name || ts.employeename || "Unassigned") : "Unassigned";
      if (!map[key]) map[key] = [];
      map[key].push(ts);
    });
    return Object.keys(map).map((k, i) => {
      const totalHours = map[k].reduce((acc, curr) => acc + (parseFloat(curr.hours) || 0), 0);
      return {
        label: k,
        color: colors[i % colors.length],
        bg: bgs[i % bgs.length],
        projects: map[k],
        totalHours
      };
    });
  }, [filteredData, groupBy]);

  const activeFilterCount = Object.values(advancedFilters).filter(v => v !== "").length;

  return (
    <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(p => !p)} title="All Timesheets">
      {/* <TopNavBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} toggleSidebar={() => setSidebarOpen(p => !p)} /> */}

      <div className="pm-page" style={{ padding: 0 }}>
        {/* FILTERS & CONTROLS */}
        <div className="pm-filters" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'transparent', padding: '0 0 16px 0', borderBottom: 'none' }}>

          <div className="pm-search-wrap" style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', minWidth: '220px' }}>
            <CiSearch size={16} style={{ color: '#64748b', marginRight: '8px' }} />
            <input
              className="pm-search-input"
              type="text"
              placeholder="Search employee..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '13.5px', color: '#1e293b', width: '100%' }}
            />
            {searchQuery && <button className="pm-search-clear" onClick={() => setSearchQuery("")}><FiX size={13} /></button>}
          </div>

          <button
            className={`pm-btn-filter-action ${activeFilterCount > 0 ? "has-filters" : ""}`}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '13.5px', color: '#475569', fontWeight: 550 }}
            onClick={() => setShowFilterDrawer(true)}>
            <FiFilter size={15} style={{ color: '#64748b' }} /> Filter
            {activeFilterCount > 0 && <span className="pm-filter-badge" style={{ background: '#f59e0b', color: '#fff', borderRadius: '99px', padding: '1px 6px', fontSize: '11px', fontWeight: 700, marginLeft: 4 }}>{activeFilterCount}</span>}
          </button>

          <div className="pm-groupby-container" ref={groupByRef} style={{ position: 'relative' }}>
            <button className="pm-btn-groupby-action" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '13.5px', color: '#475569', fontWeight: 550 }} onClick={() => setShowGroupByDropdown(!showGroupByDropdown)}>
              <FiLayers size={15} style={{ color: '#64748b' }} /> Group by: <span style={{ textTransform: 'capitalize' }}>{groupBy}</span>
            </button>
            {showGroupByDropdown && (
              <div className="pm-groupby-dropdown" style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 100, minWidth: '150px', padding: '4px 0' }}>
                {["none", "project", "department", "designation", "employee"].map(opt => (
                  <div key={opt} className={`pm-groupby-opt ${groupBy === opt ? 'active' : ''}`} style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: groupBy === opt ? '#f1f5f9' : 'transparent', fontWeight: groupBy === opt ? 600 : 400, textTransform: 'capitalize' }} onClick={() => { setGroupBy(opt); setShowGroupByDropdown(false); }}>
                    {opt}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
            <div style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '13px', fontWeight: 600, padding: '6px 12px', borderRadius: '6px' }}>
              {filteredData.length} Timesheets
            </div>

            <div className="pm-view-toggle" style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
              <button
                className={`pm-view-btn ${viewMode === "kanban" ? "active" : ""}`}
                style={{ border: 'none', padding: '8px 12px', background: viewMode === "kanban" ? '#f1f5f9' : 'transparent', color: viewMode === "kanban" ? '#4f46e5' : '#64748b', cursor: 'pointer' }}
                onClick={() => setViewMode("kanban")}
                title="Kanban View"
              >
                <FiGrid size={15} />
              </button>
              <button
                className={`pm-view-btn ${viewMode === "list" ? "active" : ""}`}
                style={{ border: 'none', padding: '8px 12px', background: viewMode === "list" ? '#f1f5f9' : 'transparent', color: viewMode === "list" ? '#4f46e5' : '#64748b', cursor: 'pointer', borderLeft: '1px solid #e2e8f0' }}
                onClick={() => setViewMode("list")}
                title="List View"
              >
                <FiList size={15} />
              </button>
              <button
                className="pm-view-btn"
                style={{ border: 'none', padding: '8px 12px', background: 'transparent', color: '#64748b', cursor: 'pointer', borderLeft: '1px solid #e2e8f0' }}
                onClick={exportTimesheetsCSV}
                title="Export Timesheets to CSV"
              >
                <FiDownload size={15} />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="pm-loading">
            <div className="pm-spinner" />
            <span style={{ color: '#94a3b8' }}>Loading timesheets...</span>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="pm-empty">
            <div className="pm-empty-icon">📂</div>
            <h3>No timesheets found</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        ) : viewMode === "kanban" ? (
          <div className="pm-kanban-board">
            {groupedData.map((col, idx) => (
              <div key={col.label} className="pm-kanban-col">
                <div className="pm-kanban-col-header" style={{
                  background: col.bg, color: col.color, padding: `12px 20px 12px ${idx === 0 ? '16px' : '24px'}`,
                  clipPath: idx === 0 ? 'polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 0% 50%)' : 'polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 12px 50%)',
                  display: 'flex', alignItems: 'center', gap: '8px', borderBottom: 'none'
                }}>
                  <span className="pm-kanban-col-title" style={{ color: col.color, fontSize: '13.5px', fontWeight: 700 }}>{col.label}</span>
                  <span className="pm-kanban-col-count" style={{ background: '#fff', color: col.color, border: `1px solid ${col.color}`, fontWeight: 700, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', minWidth: '22px', textAlign: 'center' }}>{col.projects.length}</span>
                </div>

                <div className="pm-kanban-cards">
                  {col.projects.length === 0 ? (
                    <div className="pm-kanban-empty">No timesheets</div>
                  ) : (
                    col.projects.map(ts => (
                      <div
                        key={ts.sftimesheetsid}
                        className="pm-kanban-card"
                        style={{ borderLeft: `4px solid ${col.color}`, display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                        onClick={() => setSelectedTs(ts)}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                      >
                        <div className="pm-kc-top" style={{ marginBottom: '8px', alignItems: 'flex-start' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '8px', flex: 1 }}>
                            <span className="pm-kc-name" style={{ fontWeight: 'bold', fontSize: '13px', color: '#0f172a' }}>
                              {ts.employee_full_name || ts.employeename}
                            </span>
                            {ts.employee_id && (
                              <span style={{ fontSize: '11px', color: '#4f46e5', fontWeight: 600, background: '#eef2ff', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', width: 'fit-content' }}>
                                {ts.employee_id}
                              </span>
                            )}
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>{ts.department_name || '—'} | {ts.designation || '—'}</span>
                          </div>
                          <span className="pm-kc-status" style={{ background: '#F0FDF4', color: '#16A34A', fontSize: '10px', padding: '4px 6px', fontWeight: 'bold' }}>{ts.hours ? Number(ts.hours).toFixed(2) + ' HR' : '—'}</span>
                        </div>

                        <div className="pm-kc-meta-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '8px', marginTop: '6px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Project</span>
                            <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>{ts.project_name || ts.project || '—'}</span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Date</span>
                            <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>{formatDisplayDate(ts.starttime)}</span>
                          </div>
                        </div>

                        <div style={{ borderTop: '1px dashed #e2e8f0', margin: '4px 0 8px 0' }} />
                        <div style={{ fontSize: '11.5px', color: '#475569', background: '#f8fafc', padding: '8px', borderRadius: '6px', lineHeight: 1.4 }}>
                          {ts.comments?.replace(new RegExp('<[^>]*>', 'g'), '').substring(0, 100) || "No description provided."}{ts.comments?.length > 100 ? '...' : ''}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="pm-card">
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead style={{ backgroundColor: '#f8fafc', color: '#64748b' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', width: '26%' }}>Employee</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', width: '14%' }}>Department</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', width: '15%' }}>Project</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', width: '16%' }}>Date & Time</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', width: '9%' }}>Hours</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', width: '20%' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedData.flatMap(group => [
                    <tr key={`group-${group.label}`} style={{ background: group.bg }}>
                      <td colSpan="6" style={{ padding: '8px 16px', fontWeight: 'bold', color: group.color, borderBottom: `2px solid ${group.color}40`, fontSize: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {group.label} <span style={{ background: '#fff', color: group.color, borderRadius: '10px', fontSize: '11px', padding: '2px 8px', border: `1px solid ${group.color}` }}>{group.projects.length}</span>
                          {groupBy === 'employee' && (
                            <span style={{ marginLeft: '12px', background: '#ecfdf5', color: '#10b981', borderRadius: '10px', fontSize: '12px', padding: '2px 8px', border: '1px solid #6ee7b7' }}>
                              Total Hours: {group.totalHours.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>,
                    ...group.projects.map(ts => (
                      <tr
                        key={ts.sftimesheetsid}
                        className="hover-row"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedTs(ts)}
                      >
                        <td className="pm-name-cell">
                          <strong style={{ display: 'block', fontSize: '13.5px', color: '#0f172a' }}>
                            {ts.employee_full_name || ts.employeename}
                          </strong>
                          {ts.employee_id && (
                            <span style={{ fontSize: '11px', color: '#4f46e5', fontWeight: 600, background: '#eef2ff', padding: '1px 6px', borderRadius: '4px', display: 'inline-block', marginTop: 2 }}>
                              {ts.employee_id}
                            </span>
                          )}
                          <span className="pm-desc-snippet" style={{ display: 'block', marginTop: 2 }}>{ts.designation || '—'}</span>
                        </td>
                        <td className="pm-person">{ts.department_name || '—'}</td>
                        <td className="pm-person">
                          <span style={{ fontWeight: 600, color: group.color }}>{ts.project_name || ts.project}</span>
                        </td>
                        <td className="pm-date">
                          <div style={{ fontSize: '13px' }}>{formatDisplayDate(ts.starttime)}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>{formatTime(ts.starttime).split(',')[1]} - {formatTime(ts.endtime).split(',')[1]}</div>
                        </td>
                        <td style={{ fontWeight: '600', color: '#10b981' }}>{ts.hours ? Number(ts.hours).toFixed(2) : '—'}</td>
                        <td>
                          <div style={{ fontSize: '13px', color: '#475569', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ts.comments?.replace(new RegExp('<[^>]*>', 'g'), '').substring(0, 80) || "—"}
                          </div>
                        </td>
                      </tr>
                    ))
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showFilterDrawer && (
        <>
          <div className="pm-drawer-backdrop" onClick={() => setShowFilterDrawer(false)} />
          <div className="pm-drawer">
            <div className="pm-drawer-header">
              <span>Filter Timesheets</span>
              <button className="pm-modal-close" onClick={() => setShowFilterDrawer(false)}><FiX size={18} /></button>
            </div>
            <div className="pm-drawer-body">

              <div className="pm-filter-row">
                <label className="pm-label">Department</label>
                <select className="pm-input pm-select" value={advancedFilters.department} onChange={e => setAdvancedFilters(f => ({ ...f, department: e.target.value }))}>
                  <option value="">All Departments</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="pm-filter-row">
                <label className="pm-label">Designation</label>
                <select className="pm-input pm-select" value={advancedFilters.designation} onChange={e => setAdvancedFilters(f => ({ ...f, designation: e.target.value }))}>
                  <option value="">All Designations</option>
                  {designations.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="pm-filter-row">
                <label className="pm-label">Employee</label>
                <select className="pm-input pm-select" value={advancedFilters.employee} onChange={e => setAdvancedFilters(f => ({ ...f, employee: e.target.value }))}>
                  <option value="">All Employees</option>
                  {employees.map(emp => (
                    <option key={emp.name} value={emp.name}>
                      {emp.name} ({emp.id}) - {emp.dept} - {emp.desig}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pm-filter-row">
                <label className="pm-label">Project</label>
                <select className="pm-input pm-select" value={advancedFilters.project} onChange={e => setAdvancedFilters(f => ({ ...f, project: e.target.value }))}>
                  <option value="">All Projects</option>
                  {projects.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div style={{ marginTop: 12, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <label className="pm-label" style={{ marginBottom: 12 }}>Quick Date Filter</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button className="pm-btn pm-btn-cancel" style={{ justifyContent: 'center' }} onClick={() => applyQuickDate('today')}>Today</button>
                  <button className="pm-btn pm-btn-cancel" style={{ justifyContent: 'center' }} onClick={() => applyQuickDate('yesterday')}>Yesterday</button>
                  <button className="pm-btn pm-btn-cancel" style={{ justifyContent: 'center' }} onClick={() => applyQuickDate('this_week')}>This Week</button>
                  <button className="pm-btn pm-btn-cancel" style={{ justifyContent: 'center' }} onClick={() => applyQuickDate('this_month')}>This Month</button>
                </div>
              </div>

              <div className="pm-filter-row" style={{ marginTop: 12 }}>
                <label className="pm-label">Custom Date From</label>
                <input type="date" className="pm-input" value={advancedFilters.dateFrom} onChange={e => setAdvancedFilters(f => ({ ...f, dateFrom: e.target.value }))} />
              </div>
              <div className="pm-filter-row">
                <label className="pm-label">Custom Date To</label>
                <input type="date" className="pm-input" value={advancedFilters.dateTo} onChange={e => setAdvancedFilters(f => ({ ...f, dateTo: e.target.value }))} />
              </div>

            </div>

            <div className="pm-drawer-footer">
              <button className="pm-btn pm-btn-cancel" onClick={handleResetFilters}>
                <FiRefreshCw size={13} /> Reset
              </button>
              <button className="pm-btn pm-btn-primary" onClick={() => setShowFilterDrawer(false)}>View Results</button>
            </div>
          </div>
        </>
      )}

      {/* Timesheet View Modal */}
      {selectedTs && (
        <TimesheetViewModal ts={selectedTs} onClose={() => setSelectedTs(null)} />
      )}

    </MainLayout>
  );
}
